import { useCallback, useEffect, useMemo, useRef } from 'react';
import type mapboxgl from 'mapbox-gl';
import { Source, useMap } from 'react-map-gl';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Layer, LayerBand, LAYER_ORDER } from 'components/map/ordered-layer';
import {
  mapWellPaintAtom,
  mapWellStatusFilterAtom,
} from 'components/map/atoms';
import { mapWellPaintFor, mapWellStatusFilterFor } from './map-well-paint';
import { mapStylesIndexAtom } from 'components/map/map';
import { WellPopup } from 'components/map/well-popup';
import { standardMapStyles } from 'components/map/map-styles';
import {
  crossSectionArmedAtom,
  eligibleWellsInViewAtom,
  focusedWellUwiAtom,
  hoveredDistanceFtAtom,
  sectionModeAtom,
  sectionVerticesAtom,
  sectionWellsAtom,
  showLocationIndicatorAtom,
  toggleFocusedWellAtom,
  toggleSectionWellAtom,
  wellColorRulesAtom,
  wellFiltersAtom,
} from './atoms';
import { useCrossSection } from './use-cross-section';
import { useSectionWells } from './use-section-wells';
// The navy a well keeps when no rule speaks for it is shared with the plot's own
// fallback, so one well is one colour in both views.
import {
  DEFAULT_WELL_COLOR as CROSSING_MAP_COLOR,
  resolveWellColors,
} from './well-colors';
import { mapIconName, sectionWellIcons, wellSymbolImage } from './well-symbols';
import { useCrossSectionRequest } from './use-cross-section-request';
import {
  CLICK_SLOP_PX,
  useCrossSectionDrawing,
} from './use-cross-section-drawing';
import {
  CrossSectionWellsLayer,
  ELIGIBLE_LAYER_ID,
} from './cross-section-wells-layer';
import { distanceAlongSection, pointAtDistance } from './section-geometry';
import type { SectionVertex } from './types';

/**
 * Solid magenta: the section line has to read as a drawn geometry against the map's
 * blue laterals and grey faults, and a dash made it look provisional next to them.
 */
const LINE_COLOR = '#e83e98';
const MARKER_STROKE_COLOR = '#161616';

const LINE_SOURCE_ID = 'cross-section-line';
const LINE_LAYER_ID = 'cross-section-line-layer';
const VERTEX_SOURCE_ID = 'cross-section-vertices';
export const CROSS_SECTION_VERTEX_LAYER_ID = 'cross-section-vertices-layer';
const ORDER_LAYER_ID = 'cross-section-vertex-order-layer';
const FOCUS_LAYER_ID = 'cross-section-focused-well-layer';
const SECTION_WELLS_LAYER_ID = 'cross-section-wells-colour-layer';
/**
 * The wellbore coloured with its surface hole, per product: "color the whole wellbore not
 * just the surface points". Two layers because the tiles carry the path two ways — a real
 * `survey`, or a straight `stick` where none was run — and both have to be honoured.
 */
const SECTION_BORE_SURVEY_LAYER_ID = 'cross-section-wells-bore-survey-line';
const SECTION_BORE_STICK_LAYER_ID = 'cross-section-wells-bore-stick-line';

/**
 * Wider than the basemap's own 1.5px bore, so a coloured wellbore reads as picked out
 * rather than as the same line in a different shade.
 */
const SECTION_BORE_WIDTH = 2.5;
const INDICATOR_SOURCE_ID = 'cross-section-indicator';
const INDICATOR_LAYER_ID = 'cross-section-indicator-layer';

/**
 * How near the line, on screen, a map hover has to be to count as pointing at the
 * section. In feet at the current zoom, since what matters is what the eye reads as
 * "on the line".
 */
const HOVER_SNAP_PX = 18;

/** Feet per metre, for reading a pixel tolerance as a distance. */
const FEET_PER_METRE = 3.28084;

/**
 * How many metres a screen pixel covers at a point — measured through the map's own
 * projection, which is what makes a pixel tolerance mean the same thing at every zoom.
 *
 * @param {mapboxgl.Map} map - The map.
 * @param {mapboxgl.LngLat} at - The point to measure at.
 * @return {number} Metres per pixel.
 */
const metresPerPixel = (map: mapboxgl.Map, at: mapboxgl.LngLat): number => {
  const point = map.project(at);
  return at.distanceTo(map.unproject([point.x + 1, point.y]));
};
const ENDPOINT_SOURCE_ID = 'cross-section-endpoints';
const ENDPOINT_LAYER_ID = 'cross-section-endpoints-layer';

/** A plain vertex, in Arbitrary Line. */
const VERTEX_RADIUS = 5;

/**
 * How far above its dot the A / A′ letter is lifted, in ems of its own 16px text.
 *
 * Two values because what sits under it differs. In Arbitrary Line it clears a 5px
 * vertex. In Well to Well it has to clear the numbered badge AND the well's two-line
 * name, which now hangs above the badge rather than below it — badge to ~11.5px, name
 * from ~15px to ~35px, so the letter is centred clear of all of it.
 */
const ENDPOINT_LIFT_EM = 1.1;
const NUMBERED_ENDPOINT_LIFT_EM = 3;

/**
 * The focused well, ringed rather than recoloured — a temporary spotlight on the same
 * well. Drawn over the map's own tiles, so it finds the well wherever the map has it,
 * including a crossing the cut carries no coordinates for.
 */
const FOCUS_COLOR = '#8a3ffc';
const FOCUS_RADIUS: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  6,
  6,
  11,
  9,
  14,
  11,
];

/**
 * The section's wells, as their class's symbol in the rail's colour over the map's own
 * dot: one well, one shape, one colour, in the plot and on the map alike. Registered at
 * twice nominal size so they stay crisp where the map's own dots do.
 */
const MAP_ICON_PIXEL_RATIO = 2;

/**
 * Grown in Well to Well, where the dot has to hold a legible position number. Zoomed
 * with the map so it stays proportionate rather than swelling at basin scale.
 */
const NUMBERED_VERTEX_RADIUS: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  6,
  7,
  11,
  9,
  14,
  10,
];

const ORDER_TEXT_SIZE: mapboxgl.Expression = [
  'interpolate',
  ['linear'],
  ['zoom'],
  6,
  9,
  11,
  11,
  14,
  12,
];

/**
 * Mapbox takes coordinates as `[lng, lat]`; the wire and the atoms use lat/lon.
 *
 * @param {SectionVertex} vertex - The vertex to convert.
 * @return {Array} The coordinate pair Mapbox expects.
 */
const toCoordinate = ({ lat, lon }: SectionVertex): [number, number] => [
  lon,
  lat,
];

/**
 * How a vertex is labelled on the map: the first is A, the last is A′, and the ones
 * between run B, C, D… by position. The primed end is what gives the section its
 * orientation, which is the one thing the profile below cannot show for itself.
 *
 * In Well to Well the intermediate letters are dropped — each vertex is a named well,
 * and the number inside its dot says which position it holds — but A and A′ stay,
 * because no well name conveys the section's orientation.
 *
 * @param {number} index - The vertex's position in the line.
 * @param {number} count - How many vertices the line has.
 * @param {boolean} lettered - Whether intermediate vertices get letters.
 * @return {string | null} The label to draw, or null for no label.
 */
const vertexLabel = (
  index: number,
  count: number,
  lettered: boolean
): string | null => {
  if (index === 0) {
    return 'A';
  }
  if (index === count - 1 && count >= 2) {
    return "A'";
  }
  return lettered ? String.fromCharCode(65 + index) : null;
};

/**
 * The drawn section on the map: the line, its vertices, and the A/A′ end labels. In
 * Well to Well the vertices are the picked wells, numbered by their position in the
 * traverse.
 *
 * Mounted inside `<Map>`, so it renders nothing at all until the tool is armed and a
 * mode has been picked.
 *
 * @return {JSX.Element | null} The section geometry, or null when there is none.
 */
export const CrossSectionMapLayer = (): JSX.Element | null => {
  const armed = useAtomValue(crossSectionArmedAtom);
  const mode = useAtomValue(sectionModeAtom);
  const vertices = useAtomValue(sectionVerticesAtom);
  const setVertices = useSetAtom(sectionVerticesAtom);
  const pickedWells = useAtomValue(sectionWellsAtom);
  const toggleWell = useSetAtom(toggleSectionWellAtom);
  const toggleFocus = useSetAtom(toggleFocusedWellAtom);
  const focusedWellUwi = useAtomValue(focusedWellUwiAtom);
  const setFocusedWell = useSetAtom(focusedWellUwiAtom);
  const showIndicator = useAtomValue(showLocationIndicatorAtom);
  const setEligibleInView = useSetAtom(eligibleWellsInViewAtom);
  const [hoveredDistanceFt, setHoveredDistanceFt] = useAtom(
    hoveredDistanceFtAtom
  );
  const mapStyleIndex = useAtomValue(mapStylesIndexAtom);
  const { current: mapRef } = useMap();

  // The same request the drawer asks for, so this reads the cut from the cache rather
  // than fetching a second copy of it.
  const { request, generation } = useCrossSectionRequest();
  const { data, grouped } = useCrossSection(request, generation);
  const crossingWells = useSectionWells(grouped.wells);
  const wellColorRules = useAtomValue(wellColorRulesAtom);
  const wellFilters = useAtomValue(wellFiltersAtom);
  const setMapWellPaint = useSetAtom(mapWellPaintAtom);
  const setMapWellStatusFilter = useSetAtom(mapWellStatusFilterAtom);

  // UAT-07: where the tiles can answer the rule's attribute, the rule drives the map's
  // OWN well layers — every well, not an overlay on the section's. Only `status` is on
  // the `wells-v2` tiles, so an operator or landing-zone rule still falls to the overlay
  // below: the section knows those values for its own crossings only.
  const globalWellPaint = useMemo(
    () => mapWellPaintFor(wellColorRules),
    [wellColorRules]
  );

  // The rail's Wells filter, narrowing the map's own wells as well as the section's.
  const globalWellFilter = useMemo(
    () => mapWellStatusFilterFor(wellFilters),
    [wellFilters]
  );

  // Handed to the map only while the tool is armed, and taken back the moment it is not.
  // Gated on `armed` rather than on the rail being cleared: this component renders null
  // when disarmed but stays mounted, so its cleanup does not run — and both atoms are
  // read by every map in the app, so a leak would recolour or thin out a page the
  // section had merely been visited from.
  useEffect(() => {
    setMapWellPaint(armed ? globalWellPaint : null);
    setMapWellStatusFilter(armed ? globalWellFilter : null);
    return () => {
      setMapWellPaint(null);
      setMapWellStatusFilter(null);
    };
  }, [
    armed,
    globalWellPaint,
    globalWellFilter,
    setMapWellPaint,
    setMapWellStatusFilter,
  ]);

  // The rail's Wells rule, painted on the map as well as the plot, so the two views of
  // one section never disagree. Which well takes which icon is worked out as plain data,
  // so the rule can be tested without a map.
  const sectionWells = useMemo(() => {
    const painted = wellColorRules.some(
      (rule) => rule.enabled && rule.applyTo.map
    );
    if (!painted || crossingWells.length === 0) {
      return null;
    }
    return sectionWellIcons(
      crossingWells,
      resolveWellColors(crossingWells, wellColorRules, 'map'),
      CROSSING_MAP_COLOR
    );
  }, [crossingWells, wellColorRules]);

  // Mapbox holds its own image atlas, so each symbol is registered with the map once
  // and referred to by name after that.
  //
  // Only once the style is up: `hasImage` reads `map.style`, which does not exist until
  // then, and under React 18 this effect can run before it does. Registering on
  // `style.load` also covers a style change, which empties the atlas.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !sectionWells) {
      return undefined;
    }
    let live = true;
    const register = () => {
      const missing = [...sectionWells.icons].filter(
        ([name]) => !map.hasImage(name)
      );
      if (missing.length === 0) {
        return;
      }
      Promise.all(
        missing.map(([name, spec]) =>
          wellSymbolImage({ ...spec, pixelRatio: MAP_ICON_PIXEL_RATIO })
            .then((image) => [name, image] as const)
            .catch(() => null)
        )
      ).then((drawn) => {
        if (!live) {
          return;
        }
        drawn.forEach((entry) => {
          if (!entry || map.hasImage(entry[0])) {
            return;
          }
          map.addImage(entry[0], entry[1], {
            pixelRatio: MAP_ICON_PIXEL_RATIO,
          });
        });
      });
    };

    if (map.isStyleLoaded()) {
      register();
    }
    map.on('style.load', register);
    return () => {
      live = false;
      map.off('style.load', register);
    };
  }, [mapRef, sectionWells, mapStyleIndex]);

  const onAddVertex = useCallback(
    (vertex: SectionVertex) => setVertices((current) => [...current, vertex]),
    [setVertices]
  );

  const onMoveVertex = useCallback(
    (index: number, vertex: SectionVertex) =>
      setVertices((current) =>
        current.map((existing, i) => (i === index ? vertex : existing))
      ),
    [setVertices]
  );

  const { previewVertices } = useCrossSectionDrawing({
    // Paused while a well's card is open: the card floats over the map, so a click
    // meant for it — or for the map behind it, to be rid of it — would otherwise
    // place a vertex or pick a well. Pan and zoom are Mapbox's own and stay live.
    enabled: armed && focusedWellUwi === null,
    mode,
    vertices,
    vertexLayerId: CROSS_SECTION_VERTEX_LAYER_ID,
    // The section's own eligible-wells layer, not the map's vertical-well toggle:
    // those are off by default on most maps, which left the mode with nothing to
    // click.
    wellLayerIds: [ELIGIBLE_LAYER_ID],
    onAddVertex,
    onMoveVertex,
    // A well not yet on the section is picked; one already on it is highlighted rather
    // than dropped. Click is the highlight gesture everywhere else, and losing a vertex
    // to a missed highlight is the worse misread — removal lives in the settings list.
    onToggleWell: (well) => {
      if (pickedWells.some((picked) => picked.uwi === well.uwi)) {
        toggleFocus(well.uwi);
        return;
      }
      toggleWell(well);
    },
  });

  // In Well to Well the traverse is the backend's answer, not the pick order — but the
  // line should not wait on a round trip to appear, so the picks stand in until the
  // ordered vertices land.
  const traverse = useMemo<SectionVertex[]>(() => {
    if (mode !== 'well_to_well') {
      return [];
    }
    const ordered = data?.subject.vertexLabels ?? [];
    if (ordered.length >= pickedWells.length && ordered.length > 0) {
      return ordered.map((vertex) => ({ lat: vertex.lat, lon: vertex.lon }));
    }
    // A well picked from the list has no coordinates yet, so it cannot be drawn until
    // the cut places it. Skipping it leaves the line short for a moment rather than
    // putting a vertex at nowhere.
    return pickedWells.flatMap((well) => {
      if (well.lat === null || well.lon === null) {
        return [];
      }
      return [{ lat: well.lat, lon: well.lon }];
    });
  }, [mode, data, pickedWells]);

  // What the map can offer to be picked, recounted whenever the view settles: the
  // answer is a property of where the camera is, not of the section.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || mode !== 'well_to_well') {
      setEligibleInView(null);
      return undefined;
    }

    const count = () => {
      if (!map.getLayer(ELIGIBLE_LAYER_ID)) {
        setEligibleInView(0);
        return;
      }
      // The whole viewport: what is on screen is what can be clicked.
      const canvas = map.getCanvas();
      const viewport: [mapboxgl.PointLike, mapboxgl.PointLike] = [
        [0, 0],
        [canvas.clientWidth, canvas.clientHeight],
      ];
      setEligibleInView(
        map.queryRenderedFeatures(viewport, { layers: [ELIGIBLE_LAYER_ID] })
          .length
      );
    };

    // `getLayer` reads `map.style` too, so the first count waits for the style. Without
    // it the rail's count would throw on an unready style, and `idle` alone would leave
    // it blank until the camera next moved.
    if (map.isStyleLoaded()) {
      count();
    }
    map.on('style.load', count);
    map.on('idle', count);
    return () => {
      map.off('style.load', count);
      map.off('idle', count);
    };
  }, [mapRef, mode, setEligibleInView]);

  // Hovering near the line on the map marks the corresponding distance on the
  // profile — the same shared position the plot writes when the pointer is over it.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !showIndicator) {
      return undefined;
    }

    const handleMove = (event: mapboxgl.MapMouseEvent) => {
      const line = shownRef.current;
      const at = distanceAlongSection(line, {
        lat: event.lngLat.lat,
        lon: event.lngLat.lng,
      });
      if (!at) {
        return;
      }
      // A hover in open map is not a position on the section, so the tolerance is
      // read in pixels at the current zoom rather than in feet.
      const perFoot = metresPerPixel(map, event.lngLat) * FEET_PER_METRE;
      setHoveredDistanceFt(
        at.offsetFt <= HOVER_SNAP_PX * perFoot ? at.distanceFt : null
      );
    };
    const handleLeave = () => setHoveredDistanceFt(null);

    // Held here: a removed map no longer has a canvas to clean up from.
    const canvas = map.getCanvas();
    map.on('mousemove', handleMove);
    canvas.addEventListener('mouseleave', handleLeave);

    return () => {
      map.off('mousemove', handleMove);
      canvas.removeEventListener('mouseleave', handleLeave);
    };
  }, [mapRef, showIndicator, setHoveredDistanceFt]);

  // A click on the map dismisses the well card and hands the map back — the gesture
  // people already use to be rid of a popup, and the card's own close button is a long
  // way from where they are looking. Drawing is paused while the card is up, so this
  // click only dismisses: it cannot also place a vertex or pick a well.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || focusedWellUwi === null) {
      return undefined;
    }
    // Only a press that begins after the card is up counts. The press that opened it
    // is already in flight, and its release would close the card in the same gesture.
    let pressAt: { x: number; y: number } | null = null;
    const press = (event: mapboxgl.MapMouseEvent) => {
      pressAt = { x: event.point.x, y: event.point.y };
    };
    const release = (event: mapboxgl.MapMouseEvent) => {
      if (!pressAt) {
        return;
      }
      // A pan travels; a dismissal does not. Panning with the card up is reading the
      // map around the well, which is no reason to take the card away.
      const travelled = Math.hypot(
        event.point.x - pressAt.x,
        event.point.y - pressAt.y
      );
      pressAt = null;
      if (travelled <= CLICK_SLOP_PX) {
        setFocusedWell(null);
      }
    };
    map.on('mousedown', press);
    map.on('mouseup', release);
    return () => {
      map.off('mousedown', press);
      map.off('mouseup', release);
    };
  }, [mapRef, focusedWellUwi, setFocusedWell]);

  // Mid-drag the map shows where the vertex is being taken; everything else still
  // shows where it was left.
  const shown =
    mode === 'well_to_well' ? traverse : (previewVertices ?? vertices);
  const numbered = mode === 'well_to_well';

  // Read inside the map handler below, which is registered once rather than on every
  // edit to the line.
  const shownRef = useRef(shown);
  shownRef.current = shown;

  const lineData = useMemo<GeoJSON.Feature<GeoJSON.LineString> | null>(() => {
    if (shown.length < 2) {
      return null;
    }
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: shown.map(toCoordinate) },
    };
  }, [shown]);

  const vertexData = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: shown.map((vertex, index) => ({
        type: 'Feature',
        // 1-based, so the number reads as a position — the same key the profile
        // label and the wells list row use, so one well can be followed across all
        // three.
        properties: { index, position: String(index + 1) },
        geometry: { type: 'Point', coordinates: toCoordinate(vertex) },
      })),
    }),
    [shown]
  );

  // Where the shared position falls on the map. Null unless the indicator is on and
  // something is pointing at the section.
  const indicatorData = useMemo<GeoJSON.Feature<GeoJSON.Point> | null>(() => {
    if (!showIndicator || hoveredDistanceFt === null) {
      return null;
    }
    const at = pointAtDistance(shown, hoveredDistanceFt);
    if (!at) {
      return null;
    }
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'Point', coordinates: toCoordinate(at) },
    };
  }, [showIndicator, hoveredDistanceFt, shown]);

  // The well the card is about, in the shape the map's own popup takes: it looks the
  // well up itself, so the uwi is all it needs. Held by identity, which is what the
  // popup keys its lookup on — a fresh object each render would refetch each render.
  const focusedWells = useMemo(
    () => (focusedWellUwi === null ? null : { [focusedWellUwi]: undefined }),
    [focusedWellUwi]
  );

  // Where the card hangs: the pick's own surface hole where the pick knows it, and
  // otherwise the point where the section cuts the well — which is the crossing the
  // plot is showing at the same moment, so both cards are about the same place.
  const focusedWellPoint = useMemo<SectionVertex | null>(() => {
    if (focusedWellUwi === null) {
      return null;
    }
    const picked = pickedWells.find((well) => well.uwi === focusedWellUwi);
    if (picked && picked.lat !== null && picked.lon !== null) {
      return { lat: picked.lat, lon: picked.lon };
    }
    const crossing = crossingWells.find((well) => well.uwi === focusedWellUwi);
    if (!crossing) {
      return null;
    }
    return pointAtDistance(shown, crossing.xOffsetFt);
  }, [focusedWellUwi, pickedWells, crossingWells, shown]);

  const endpointData = useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: shown.flatMap((vertex, index) => {
        const label = vertexLabel(index, shown.length, !numbered);
        if (label === null) {
          return [];
        }
        return [
          {
            type: 'Feature' as const,
            properties: { label },
            geometry: {
              type: 'Point' as const,
              coordinates: toCoordinate(vertex),
            },
          },
        ];
      }),
    }),
    [shown, numbered]
  );

  if (!armed || !mode || !mapRef) {
    return null;
  }

  const labelFont = standardMapStyles[mapStyleIndex].fonts.CONDENSED_BOLD;

  return (
    // Above the wells and everything else the shared map draws: the section is what
    // the user is working on, and a well symbol over the line they are dragging
    // makes the line look broken.
    <>
      {/* The wells this mode is built from. Mounted only here: in Arbitrary Line a
          click is a vertex, and drawing wells a click cannot pick would promise a
          gesture the map does not honour. */}
      {numbered && <CrossSectionWellsLayer />}
      <LayerBand band={LAYER_ORDER.crossSection}>
        {lineData && (
          <Source id={LINE_SOURCE_ID} type="geojson" data={lineData}>
            <Layer
              id={LINE_LAYER_ID}
              type="line"
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{ 'line-color': LINE_COLOR, 'line-width': 2.5 }}
            />
          </Source>
        )}
        <Source id={VERTEX_SOURCE_ID} type="geojson" data={vertexData}>
          <Layer
            id={CROSS_SECTION_VERTEX_LAYER_ID}
            type="circle"
            paint={{
              'circle-radius': numbered
                ? NUMBERED_VERTEX_RADIUS
                : VERTEX_RADIUS,
              'circle-color': LINE_COLOR,
              'circle-stroke-color': MARKER_STROKE_COLOR,
              'circle-stroke-width': 1.5,
            }}
          />
          {/* The position number, set inside the dot rather than floating above it: a
            badge over the dot collides with the A / A′ labels at the two ends, and
            the dot is the thing being numbered. Drawn after the dots, so the ring
            around a picked well stays visible underneath its number. */}
          {numbered && (
            <Layer
              id={ORDER_LAYER_ID}
              type="symbol"
              layout={{
                'text-field': ['get', 'position'],
                'text-size': ORDER_TEXT_SIZE,
                'text-anchor': 'center',
                'text-font': labelFont,
                // Never dropped for collision: a missing number misnumbers the whole
                // traverse by omission, which is worse than a crowded one.
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{ 'text-color': MARKER_STROKE_COLOR }}
            />
          )}
        </Source>
        {/* The wellbore in the rail's colour, for the attributes the tiles cannot answer
            — operator and landing zone, which the section knows only for its own
            crossings. Absent when the rule is one the map's own layers have taken over,
            since colouring the same bore twice is what UAT-07 objected to. */}
        {sectionWells &&
          globalWellPaint === null &&
          (
            [
              [SECTION_BORE_SURVEY_LAYER_ID, 'survey', null],
              // Only where no survey was run. The tiles carry a stick for every well,
              // surveyed or not, so without this guard a surveyed well would be drawn
              // twice — its real path and a straight line cutting the corner off it.
              [
                SECTION_BORE_STICK_LAYER_ID,
                'stick',
                ['==', ['get', 'has_survey'], false],
              ],
            ] as const
          ).map(([layerId, sourceLayer, extraFilter]) => (
            <Layer
              key={layerId}
              id={layerId}
              type="line"
              source="wells-v2"
              source-layer={sourceLayer}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              filter={[
                'all',
                [
                  'in',
                  ['get', 'uwi'],
                  [
                    'literal',
                    sectionWells.colorByUwi.filter(
                      (_, index) => index % 2 === 0
                    ),
                  ],
                ],
                ...(extraFilter ? [extraFilter] : []),
              ]}
              paint={{
                'line-color': [
                  'match',
                  ['get', 'uwi'],
                  ...sectionWells.colorByUwi,
                  CROSSING_MAP_COLOR,
                ] as unknown as mapboxgl.Expression,
                'line-width': SECTION_BORE_WIDTH,
              }}
            />
          ))}
        {/* The section's wells, each as its class's symbol in the rail's colour, over
            the map's own dot for the same well. Under the focus ring, which has to
            stay legible on top of a symbol. */}
        {sectionWells && (
          <Layer
            id={SECTION_WELLS_LAYER_ID}
            type="symbol"
            source="wells-v2"
            source-layer="surface-hole"
            filter={[
              'in',
              ['get', 'uwi'],
              [
                'literal',
                sectionWells.iconByUwi.filter((_, index) => index % 2 === 0),
              ],
            ]}
            layout={{
              // The last argument is the fallback Mapbox requires for a `match`; a
              // well without an icon is filtered out above, so it never applies.
              'icon-image': [
                'match',
                ['get', 'uwi'],
                ...sectionWells.iconByUwi,
                mapIconName('filledCircle', CROSSING_MAP_COLOR),
              ] as unknown as mapboxgl.Expression,
              // Never dropped for collision: a section's own wells are the point of
              // the view, and a symbol quietly withheld reads as a missing well.
              'icon-allow-overlap': true,
              'icon-ignore-placement': true,
            }}
          />
        )}
        {/* The focused well, found in the map's own well source by its uwi. */}
        {focusedWellUwi !== null && (
          <Layer
            id={FOCUS_LAYER_ID}
            type="circle"
            source="wells-v2"
            source-layer="surface-hole"
            filter={['==', ['get', 'uwi'], focusedWellUwi]}
            paint={{
              'circle-radius': FOCUS_RADIUS,
              'circle-color': 'rgba(0, 0, 0, 0)',
              'circle-stroke-color': FOCUS_COLOR,
              'circle-stroke-width': 2.5,
            }}
          />
        )}
        {indicatorData && (
          <Source id={INDICATOR_SOURCE_ID} type="geojson" data={indicatorData}>
            {/* White with a dark ring: the marker has to read on the section line it
              sits on as well as on open map. */}
            <Layer
              id={INDICATOR_LAYER_ID}
              type="circle"
              paint={{
                'circle-radius': 6,
                'circle-color': '#ffffff',
                'circle-stroke-color': MARKER_STROKE_COLOR,
                'circle-stroke-width': 2,
                'circle-opacity': 0.9,
              }}
            />
          </Source>
        )}
        <Source id={ENDPOINT_SOURCE_ID} type="geojson" data={endpointData}>
          <Layer
            id={ENDPOINT_LAYER_ID}
            type="symbol"
            layout={{
              'text-field': ['get', 'label'],
              'text-size': 16,
              // Lifted clear of the dot it belongs to, rather than centred on it —
              // by how much depends on which dot that is.
              'text-offset': [
                0,
                -(numbered ? NUMBERED_ENDPOINT_LIFT_EM : ENDPOINT_LIFT_EM),
              ],
              'text-allow-overlap': true,
              // And invisible to the collision engine, not merely exempt from it.
              // A Well to Well endpoint is a picked well, whose name is drawn above
              // its badge — into the space this letter reserves. The letter always
              // draws either way; without this it silently costs that well its name.
              'text-ignore-placement': true,
              'text-font': labelFont,
            }}
            paint={{
              'text-color': MARKER_STROKE_COLOR,
              'text-halo-color': '#ffffff',
              'text-halo-width': 1.5,
            }}
          />
        </Source>
      </LayerBand>
      {/* The map's own well card, for the well the section is asking about — the same
          card the map gives for a well anywhere else, so one well reads one way. It
          holds the map until it is closed. */}
      {focusedWells !== null && focusedWellPoint !== null && (
        <WellPopup
          wells={focusedWells}
          latitude={focusedWellPoint.lat}
          longitude={focusedWellPoint.lon}
          closeButton
          onClose={() => setFocusedWell(null)}
        />
      )}
    </>
  );
};
